import type { LiteSecodaEntityOut } from '@repo/api-codegen';
import type { BreadcrumbCrumb } from '@repo/foundations';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useBiDashboardBreadcrumbs } from '../../BiDashboard/hooks/useBiDashboardBreadcrumbs';

interface UseBiMetricBreadcrumbsProps {
	metricId?: string;
	entity?: LiteSecodaEntityOut;
}

export function useBiMetricBreadcrumbs({
	metricId,
	entity,
}: UseBiMetricBreadcrumbsProps) {
	const [searchParams] = useSearchParams();
	const dashboardId = searchParams.get('dashboardId') ?? undefined;

	// Get dashboard breadcrumbs if dashboardId is provided
	const { breadcrumbs: dashboardBreadcrumbs, dashboard } =
		useBiDashboardBreadcrumbs({
			dashboardId,
		});

	const breadcrumbs: BreadcrumbCrumb[] | undefined = useMemo(() => {
		// If dashboard breadcrumbs exist, extend them with the metric
		if (dashboardBreadcrumbs) {
			return [
				...dashboardBreadcrumbs,
				{
					title: entity?.title ?? 'Untitled Metric',
					href:
						`/bi/metric/${metricId}` +
						(dashboardId ? `?dashboardId=${dashboardId}` : ''),
				},
			];
		}

		// Otherwise, return simple breadcrumbs: Metrics → Metric name
		return [
			{
				title: 'Metrics',
				href: '/',
			},
			{
				title: entity?.title ?? 'Untitled Metric',
				href:
					`/bi/metric/${metricId}` +
					(dashboardId ? `?dashboardId=${dashboardId}` : ''),
			},
		];
	}, [dashboardBreadcrumbs, entity?.title, metricId, dashboardId]);

	return {
		breadcrumbs,
		dashboard,
	};
}
